import { SegmentHierarchy } from 'src/models/AppContextModels';

/**
 * Filters segment hierarchy data to only include leaf nodes.
 * A leaf node is one that is not a parent of any other node.
 *
 * Uses a Set of parent keys for O(n) filtering — first pass builds the parent set,
 * second pass filters out any node whose key appears in that set.
 *
 * @param segments - Array of segment hierarchy entries
 * @returns Only the leaf nodes from the input array
 */
export const filterToLeafNodes = (segments: SegmentHierarchy[]): SegmentHierarchy[] => {
  const parentKeys = new Set<string>();
  for (const segment of segments) {
    const h = segment.segment_hierarchy;
    for (let i = 1; i < h.length; i++) {
      parentKeys.add(h.slice(0, i).join(','));
    }
  }
  return segments.filter((segment) => !parentKeys.has(segment.segment_hierarchy.join(',')));
};

/**
 * Filters master hierarchy data to include only descendants of selected parent nodes.
 *
 * @param masterSegments - Full hierarchy data from master source
 * @param selectedParentNodes - Parent nodes to filter by
 * @param includeParent - Whether to include the exact parent nodes in results (default: false)
 * @returns Filtered hierarchy entries that are descendants of selected parents
 */
export const filterDescendantsOfSelectedNodes = (
  masterSegments: SegmentHierarchy[],
  selectedParentNodes: SegmentHierarchy[],
  includeParent: boolean = false
): SegmentHierarchy[] => {
  const selectedKeys = selectedParentNodes.map((seg) => seg.segment_hierarchy.join(','));
  const selectedKeySet = new Set(selectedKeys);

  return masterSegments.filter((segment) => {
    const segmentKey = segment.segment_hierarchy.join(',');
    const isDescendantOrSame = selectedKeys.some((parentKey) => segmentKey === parentKey || segmentKey.startsWith(parentKey + ','));

    if (!includeParent) {
      return isDescendantOrSame && !selectedKeySet.has(segmentKey);
    }
    return isDescendantOrSame;
  });
};
